import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, X, FileText, Image as ImageIcon, Video, Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface UploadedAttachment {
  name: string;
  path: string;
  mime: string;
  size: number;
}

interface Props {
  bucket: "contact-attachments" | "support-attachments";
  value: UploadedAttachment[];
  onChange: (next: UploadedAttachment[]) => void;
  maxFiles?: number;
  disabled?: boolean;
  compact?: boolean;
}

const MAX_FILE_BYTES = 25 * 1024 * 1024;

const iconFor = (mime: string) => {
  if (mime.startsWith("image/")) return <ImageIcon className="w-4 h-4" />;
  if (mime.startsWith("video/")) return <Video className="w-4 h-4" />;
  return <FileText className="w-4 h-4" />;
};

const AttachmentPicker = ({ bucket, value, onChange, maxFiles = 10, disabled, compact }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = maxFiles - value.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${maxFiles} fichiers`);
      return;
    }
    const list = Array.from(files).slice(0, remaining);
    setUploading(true);
    try {
      const uploaded: UploadedAttachment[] = [];
      for (const file of list) {
        if (file.size > MAX_FILE_BYTES) {
          toast.error(`${file.name} dépasse 25 Mo`);
          continue;
        }
        const { data: signData, error: signErr } = await supabase.functions.invoke("support-sign-upload", {
          body: { bucket, filename: file.name, size: file.size },
        });
        if (signErr || !signData?.path) {
          toast.error(`Échec préparation : ${file.name}`);
          continue;
        }
        const { error: upErr } = await supabase.storage
          .from(bucket)
          .uploadToSignedUrl(signData.path, signData.token, file, {
            contentType: file.type || "application/octet-stream",
            upsert: false,
          });
        if (upErr) {
          console.error("upload error", upErr);
          toast.error(`Upload échoué : ${file.name}`);
          continue;
        }
        uploaded.push({
          name: file.name,
          path: signData.path,
          mime: file.type || "application/octet-stream",
          size: file.size,
        });
      }
      onChange([...value, ...uploaded]);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (path: string) => onChange(value.filter((a) => a.path !== path));

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/*,application/pdf,.doc,.docx,.txt,.zip"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled || uploading}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size={compact ? "sm" : "default"}
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading || value.length >= maxFiles}
          className="rounded-xl"
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1.5" />}
          Ajouter un fichier
        </Button>
        <span className="text-xs text-muted-foreground">
          {value.length}/{maxFiles} · 25 Mo max par fichier
        </span>
      </div>
      {value.length > 0 && (
        <ul className="space-y-1.5">
          {value.map((a) => (
            <li
              key={a.path}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-muted/60 text-sm"
            >
              <span className="text-primary shrink-0">{iconFor(a.mime)}</span>
              <span className="truncate flex-1 min-w-0">{a.name}</span>
              <span className="text-xs text-muted-foreground shrink-0">
                {(a.size / 1024 / 1024).toFixed(2)} Mo
              </span>
              <button
                type="button"
                onClick={() => remove(a.path)}
                disabled={disabled}
                className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                aria-label={`Retirer ${a.name}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {value.length >= maxFiles && (
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Limite atteinte
        </p>
      )}
    </div>
  );
};

export default AttachmentPicker;
